import { useState } from "react";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GenerateBookingReportDialog } from "@/app/Suppliers/Components/GenerateBookingReportDialog";
import {
  SupplierBookingReportResult,
  supplierBookingReportsQuery,
  generateSupplierBookingReportMutation,
  resendSupplierBookingReportMutation,
} from "@/app/Suppliers/GraphQL/supplierBookingReportsQuery";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { Button } from "@/components/catalyst/button";
import {
  PlusCircleIcon,
  EyeIcon,
  RefreshCwIcon,
  DownloadIcon,
  SendIcon,
} from "lucide-react";
import { formatDate } from "@/lib/Formatters/formatDate";
import { PreviewPdfDialog } from "@/lib/Components/Dialog/PreviewPdfDialog";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { SortOrder } from "@/gql/graphql";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { toast } from "sonner";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getYearFilterOptions() {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 3 }, (_, i) => ({
    label: String(currentYear - i),
    value: String(currentYear - i),
  }));
}

export function SupplierBookingReportsTable({
  supplierId: supplierIdProp,
}: {
  supplierId?: string;
}) {
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const supplierId = supplierIdProp ?? globalSupplierId ?? undefined;
  const { open: openGenerateDialog } = useDialog(GenerateBookingReportDialog);
  const { open: openPdfPreview } = useDialog(PreviewPdfDialog);
  const { mutateAsync: regenerate } = useGqlMutation(
    generateSupplierBookingReportMutation,
  );
  const { mutateAsync: resend } = useGqlMutation(
    resendSupplierBookingReportMutation,
  );

  const [yearFilter, setYearFilter] = useState<string[]>([]);

  const columns: DataTableColDef<SupplierBookingReportResult, any, any>[] = [
    ...(supplierId
      ? []
      : [
          {
            id: "supplier",
            header: "Supplier",
            accessorFn: (data: SupplierBookingReportResult) =>
              data.supplier.name,
          },
        ]),
    {
      id: "period",
      accessorFn: (data) => `${MONTH_NAMES[data.month - 1]} ${data.year}`,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Period" />
      ),
    },
    {
      id: "created_at",
      accessorFn: (data) => formatDate(data.created_at),
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Generated" />
      ),
    },
    {
      id: "pdf",
      header: "PDF",
      cell: ({ row }) => {
        const pdfUrl = row.original.pdf_url;
        return pdfUrl ? (
          <Button
            plain
            onClick={() => openPdfPreview({ url: pdfUrl })}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <EyeIcon className="size-4" />
            Preview
          </Button>
        ) : (
          <span className="text-sm text-gray-400">Pending</span>
        );
      },
    },
    {
      id: "excel",
      header: "Excel",
      cell: ({ row }) => {
        const excelUrl = row.original.excel_url;
        return excelUrl ? (
          <a
            href={excelUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-800"
          >
            <DownloadIcon className="size-4" />
            Download
          </a>
        ) : (
          <span className="text-sm text-gray-400">Pending</span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const report = row.original;
        return (
          <div className="flex items-center gap-1">
            <Button
              plain
              title="Resend email"
              onClick={async () => {
                await resend({ id: report.id });
                toast.success("Report email is being resent.");
              }}
            >
              <SendIcon className="size-4" />
            </Button>
            <Button
              plain
              title="Regenerate report"
              onClick={async () => {
                await regenerate({
                  supplierId: report.supplier.id,
                  month: report.month,
                  year: report.year,
                });
                toast.success("Report is being regenerated.");
              }}
            >
              <RefreshCwIcon className="size-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      id="supplier-booking-reports"
      initialSorting={[{ id: "period", desc: true }]}
      title="Booking Reports"
      document={supplierBookingReportsQuery}
      accessor={(data) => data.supplierBookingReports}
      columns={columns}
      filters={(table) => (
        <div className="flex space-x-2">
          <MultiSelectFilter
            label="Year"
            options={getYearFilterOptions()}
            selected={yearFilter}
            onChange={(newYears) => {
              setYearFilter(newYears);
              table.setPagination((prev) => ({
                ...prev,
                pageIndex: 0,
              }));
            }}
          />
        </div>
      )}
      getQueryVariables={({ pagination, sorting }) => {
        const orderBy =
          sorting.length > 0
            ? sorting.flatMap((s) => {
                const order = s.desc ? SortOrder.Desc : SortOrder.Asc;
                if (s.id === "period") {
                  return [
                    { column: "year", order },
                    { column: "month", order },
                  ];
                }
                return [{ column: s.id, order }];
              })
            : [
                { column: "year", order: SortOrder.Desc },
                { column: "month", order: SortOrder.Desc },
              ];

        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          supplierId: supplierId ?? undefined,
          year: yearFilter.length === 1 ? parseInt(yearFilter[0]) : undefined,
          orderBy,
        };
      }}
      rightButtons={
        <Button
          type="button"
          onClick={() => {
            openGenerateDialog({ supplierId });
          }}
          LeadingIcon={PlusCircleIcon}
        >
          Generate Report
        </Button>
      }
    />
  );
}
